/**
 * ของตกแต่งเพิ่มเติมรายโซน — ภูเขารอบขอบแผนที่ ทางดินไปประตู พุ่มไม้ ขอนไม้ บ้านในเมือง ฯลฯ
 *
 * แยกจาก world.ts เพราะยาว และใช้ตัวสุ่มของตัวเอง (seed แยก) จึงไม่ไปขยับผังต้นไม้เดิมที่ผู้เล่นคุ้นแล้ว
 * **ทุกชิ้นส่งเข้า `add` เพื่อรวมเป็นก้อน deco เดียว** และของที่สูงเกิน 1.2 ต้องผ่าน `blocked`
 * (ห้ามทับวงเกิดมอนและหน้าประตู — มีเทสต์ใน world.test.ts ตรวจจากจุดยอดจริง)
 */

import type { Scene } from "@babylonjs/core/scene";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import type { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { distToSeg, PORTAL_RADIUS, type ZoneDef } from "@wickhaven/shared";

export interface PropCtx {
  scene: Scene;
  zone: ZoneDef | undefined;
  town: boolean;
  rand: () => number;
  add: (m: Mesh) => void;
  /** จุดนี้วางของสูงไม่ได้ (วงเกิด ประตู ลาน บ่อ ทางในเมือง) */
  blocked: (x: number, z: number) => boolean;
  /** เฉพาะวงเกิดมอนกับหน้าประตู (ใช้กับเสาไฟริมทางในเมืองซึ่งตั้งใจวางชิดทาง) */
  clear: (x: number, z: number) => boolean;
  /** เครือข่ายทางจาก shared `routeNetwork` (ว่างในเมือง) */
  routes: Seg[];
  /** ครึ่งความกว้างแผนที่ */
  half: number;
  /** ระยะถึงขอบพื้นที่เดินได้ (ลบ = ในทาง) — ของสูงในโซนนอกต้องอยู่นอกทาง ไม่งั้นบังทางที่เดินได้จริง */
  wall: (x: number, z: number) => number;
  mat: (hex: string, opts?: { em?: string; alpha?: number; noLight?: boolean }) => StandardMaterial;
  flat: (m: Mesh) => Mesh;
}

/** ธีมของภูเขาขอบแผนที่ */
const MOUNTAIN: Record<string, { rock: string; cap?: string; peak: number }> = {
  meadow: { rock: "#6f8a5a", cap: "#9cbf7a", peak: 14 },
  hills: { rock: "#7d8f5c", cap: "#a9c47c", peak: 18 },
  forest: { rock: "#3c5a33", cap: "#2d4a28", peak: 16 },
  coast: { rock: "#a99c7c", cap: "#7c9b5a", peak: 12 },
  root_cave: { rock: "#3b2f24", peak: 20 },
  desert: { rock: "#c9955f", cap: "#e0b47a", peak: 15 },
  dungeon_mine: { rock: "#33291f", peak: 20 },
  ruins: { rock: "#7a776c", cap: "#8f8c80", peak: 15 },
  swamp: { rock: "#3f4535", cap: "#4d5a3d", peak: 12 },
  crystal_cave: { rock: "#252c40", peak: 20 },
  snow: { rock: "#7f93a6", cap: "#f4f8fb", peak: 22 },
  dungeon_frost: { rock: "#4a6680", cap: "#e6f2fa", peak: 20 },
  volcano: { rock: "#2a1c17", cap: "#ff5a1f", peak: 22 },
  abyss_ruins: { rock: "#1a1427", cap: "#6b46c1", peak: 18 },
  storm: { rock: "#4a505c", cap: "#8a92a4", peak: 24 },
  jungle: { rock: "#3a5a3a", cap: "#2a6b2a", peak: 18 },
  graveyard: { rock: "#4a4a4c", cap: "#6a6a70", peak: 14 },
  celestial: { rock: "#e0dccf", cap: "#ffffff", peak: 20 },
};


export function buildProps(c: PropCtx): void {
  const id = c.zone?.theme ?? c.zone?.id ?? "meadow";
  const trail = c.routes;
  wornGround(c, id, trail);
  const onTrail = (x: number, z: number): boolean => trail.some((s) => distToSeg(x, z, s) < 2.4);
  // ตรวจทั้งรอยเท้าของชิ้น ไม่ใช่แค่จุดกลาง (บ้าน/ซากกำแพงกว้างหลายหน่วย)
  const freeTall = (x: number, z: number, r = 2.2): boolean => {
    for (let k = 0; k <= 8; k++) {
      const a = (k / 8) * Math.PI * 2;
      const px = k === 8 ? x : x + Math.cos(a) * r;
      const pz = k === 8 ? z : z + Math.sin(a) * r;
      if (c.blocked(px, pz) || onTrail(px, pz) || (!c.town && c.wall(px, pz) < 0.5)) return false;
    }
    return true;
  };

  buildMountains(c, id);
  if (c.town) buildTown(c, freeTall);

  // พุ่ม/ของเตี้ยรายโซน กระจายทั่ว (เตี้ยกว่า 1.2 จึงวางในวงเกิดได้ แต่หลบทางกับประตู)
  const S = c.half / 46;
  const lowCount = Math.round(70 * S * 1.5);
  for (let i = 0; i < lowCount; i++) {
    const x = (c.rand() * 88 - 44) * S;
    const z = (c.rand() * 88 - 44) * S;
    if (onTrail(x, z) || nearPortal(c, x, z, 2) || (c.town && (Math.hypot(x, z) < 8 || Math.abs(x) < 2.6 || Math.abs(z) < 2.6))) continue;
    lowProp(c, id, x, z, 0.7 + c.rand() * 0.6);
  }

  // ของสูงรายโซน (ขอนใหญ่ ซากกำแพง กองผลึก) วางเฉพาะที่ว่าง
  for (let i = 0; i < Math.round(26 * S * 1.5); i++) {
    const x = (c.rand() * 84 - 42) * S;
    const z = (c.rand() * 84 - 42) * S;
    if (!freeTall(x, z) || (c.town && Math.hypot(x, z) < 14)) continue;
    tallProp(c, id, x, z, 0.8 + c.rand() * 0.5);
  }
}

/* ---------------- ภูเขาขอบแผนที่ ---------------- */

function buildMountains(c: PropCtx, id: string): void {
  const th = MOUNTAIN[id] ?? MOUNTAIN["meadow"]!;
  const n = Math.round(34 * (c.half / 46));
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + c.rand() * 0.12;
    const r = c.half + 8 + c.rand() * 6;
    const x = Math.cos(a) * r;
    const z = Math.sin(a) * r;
    // เว้นช่องหลังประตู ผู้เล่นเห็นว่า "ทางไปต่ออยู่ตรงนี้"
    if (nearPortal(c, x, z, 12)) continue;
    const h = th.peak * (0.55 + c.rand() * 0.6);
    const w = 8 + c.rand() * 6;
    const m = c.flat(MeshBuilder.CreateCylinder("mtn", { diameterTop: 0, diameterBottom: w, height: h, tessellation: 5 + Math.floor(c.rand() * 3) }, c.scene));
    m.position.set(x, h / 2 - 0.3, z);
    m.rotation.y = c.rand() * 3;
    m.material = c.mat(th.rock);
    c.add(m);
    if (th.cap) {
      const capH = h * 0.32;
      const cap = c.flat(MeshBuilder.CreateCylinder("mcap", { diameterTop: 0, diameterBottom: w * 0.34, height: capH, tessellation: 5 }, c.scene));
      cap.position.set(x, h - capH / 2 - 0.25, z);
      cap.rotation.y = m.rotation.y;
      cap.material = c.mat(th.cap, id === "volcano" ? { em: "#ff3d00", noLight: true } : {});
      c.add(cap);
    }
  }
}

/* ---------------- เส้นทางธรรมชาติจากจุดเกิดไปทุกประตู ---------------- */

import type { Seg } from "@wickhaven/shared";

/** ปรับความสว่างสี hex (amt -1..1) */
export function shadeHex(hex: string, amt: number): string {
  const n = parseInt(hex.replace("#", ""), 16);
  const ch = (v: number): string => {
    const out = amt < 0 ? v * (1 + amt) : v + (255 - v) * amt;
    return Math.round(Math.max(0, Math.min(255, out))).toString(16).padStart(2, "0");
  };
  return `#${ch((n >> 16) & 255)}${ch((n >> 8) & 255)}${ch(n & 255)}`;
}

/** รอยพื้นสึกจาง ๆ เป็นหย่อม ๆ มีช่วงขาด — ใบ้ทางพอสังเกต ไม่ใช่ถนน */
function wornGround(c: PropCtx, id: string, segs: Seg[]): void {
  const base = c.zone?.ground ?? "#6b9e5c";
  // รอบ 20: รอยทางเป็นสีดินโปร่งแสง (พื้นมีลายแล้ว สีอ่อนทึบดูเป็นสติกเกอร์)
  const col = id === "snow" || id === "dungeon_frost" ? shadeHex(base, -0.08) : mixHex(base, "#8a6a44", 0.45);
  for (const [ax, az, bx, bz] of segs) {
    const len = Math.hypot(bx - ax, bz - az);
    for (let t = 0; t < len; t += 2.2 + c.rand() * 2.5) {
      if (c.rand() < 0.45) continue;
      const k = t / len;
      const d = MeshBuilder.CreateDisc("worn", { radius: 0.6 + c.rand() * 1.1, tessellation: 6 }, c.scene);
      d.rotation.x = Math.PI / 2;
      d.rotation.y = c.rand() * 3;
      d.scaling.y = 0.6 + c.rand() * 0.5;
      d.position.set(ax + (bx - ax) * k + (c.rand() - 0.5) * 1.6, 0.015, az + (bz - az) * k + (c.rand() - 0.5) * 1.6);
      d.material = c.mat(col, { alpha: 0.3 });
      c.add(d);
    }
  }
}

function mixHex(a: string, b: string, t: number): string {
  const p = (h: string, i: number): number => parseInt(h.slice(1 + i * 2, 3 + i * 2), 16);
  return "#" + [0, 1, 2].map((i) => Math.round(p(a, i) * (1 - t) + p(b, i) * t).toString(16).padStart(2, "0")).join("");
}

function nearPortal(c: PropCtx, x: number, z: number, extra: number): boolean {
  return (c.zone?.portals ?? []).some((p) => Math.hypot(x - p.x, z - p.z) < PORTAL_RADIUS + extra);
}

/* ---------------- เมือง ---------------- */

const ROOFS = ["#c0533f", "#3d6cb9", "#d4982f", "#4f8a44", "#884ea0", "#b33939"];

function buildTown(c: PropCtx, free: (x: number, z: number, r?: number) => boolean): void {
  // บ้านสองวงรอบลาน อยู่ในมุมระหว่างทางเดินสี่แฉก
  const spots: Array<[number, number]> = [];
  for (const [sx, sz] of [[1, 1], [-1, 1], [1, -1], [-1, -1]] as const) {
    spots.push([sx * 11, sz * 11], [sx * 17, sz * 9], [sx * 9, sz * 17]);
  }
  for (const [x, z] of spots) {
    if (!free(x, z, 4.2) || Math.hypot(x - -26, (z - 22) / 0.7) < 9 || Math.hypot(x + 6, z - 5) < 4) continue;
    // หลบลานฝึกหุ่นฟาง
    if (Math.hypot(x - -32, z - -16) < 10) continue;
    house(c, x, z, Math.atan2(-x, -z), ROOFS[Math.floor(c.rand() * ROOFS.length)]!);
  }

  // ม้านั่งไม้พักผ่อนรอบลานเมือง 4 ทิศ
  for (const [bx, bz, bry] of [
    [5.2, 5.2, -Math.PI / 4],
    [-5.2, 5.2, Math.PI / 4],
    [5.2, -5.2, -Math.PI * 3 / 4],
    [-5.2, -5.2, Math.PI * 3 / 4],
  ] as const) {
    bench(c, bx, bz, bry);
  }

  // เสาไฟริมทางสี่ทิศ และริมทางไปลานซ้อม
  const lampSpots: Array<[number, number]> = [];
  for (const d of [9, 16, 23, 30]) {
    lampSpots.push([2.4, d], [-2.4, -d], [d, -2.4], [-d, 2.4]);
  }
  // เสาไฟทางแยกไปลานฝึกหุ่นฟาง
  lampSpots.push([-20.2, -8], [-20.2, -14], [-27, -18.2]);

  for (const [x, z] of lampSpots) {
    if (!c.clear(x, z)) continue;
    const pole = c.flat(MeshBuilder.CreateCylinder("lpole", { diameter: 0.14, height: 2.3, tessellation: 6 }, c.scene));
    pole.position.set(x, 1.15, z);
    pole.material = c.mat("#332b20");
    c.add(pole);

    const arm = c.flat(MeshBuilder.CreateBox("larm", { width: 0.08, height: 0.08, depth: 0.35 }, c.scene));
    arm.position.set(x, 2.2, z);
    arm.material = c.mat("#332b20");
    c.add(arm);

    const lamp = MeshBuilder.CreateBox("lbox", { size: 0.34 }, c.scene);
    lamp.position.set(x, 2.35, z);
    lamp.material = c.mat("#ffe58f", { em: "#ffb84d", noLight: true });
    c.add(lamp);

    const cap = c.flat(MeshBuilder.CreateCylinder("lcap", { diameterTop: 0, diameterBottom: 0.5, height: 0.18, tessellation: 4 }, c.scene));
    cap.position.set(x, 2.55, z);
    cap.material = c.mat("#28221a");
    c.add(cap);
  }

  // ถังไม้ ลังไม้ ข้างแผงร้าน และลานฝึก
  for (const [x, z] of [[-6.4, -5], [6.4, -5], [-7, 4.8], [7.2, 3], [3.4, 7.4], [-25, -15]] as const) {
    const barrel = c.flat(MeshBuilder.CreateCylinder("barrel", { diameter: 0.75, height: 0.9, tessellation: 8 }, c.scene));
    barrel.position.set(x, 0.45, z);
    barrel.material = c.mat("#8a5e34");
    c.add(barrel);
    const crate = c.flat(MeshBuilder.CreateBox("crate", { size: 0.65 }, c.scene));
    crate.position.set(x + 0.85, 0.32, z + 0.3);
    crate.rotation.y = 0.4;
    crate.material = c.mat("#b08850");
    c.add(crate);
  }

  // รั้วไม้รอบบ่อน้ำ (เตี้ย)
  for (let i = 0; i < 18; i++) {
    const a = (i / 18) * Math.PI * 2;
    if (Math.abs(Math.sin(a)) < 0.2 && Math.cos(a) > 0) continue; // ช่องเข้า
    const f = c.flat(MeshBuilder.CreateBox("fence", { width: 0.16, height: 0.85, depth: 0.16 }, c.scene));
    f.position.set(-26 + Math.cos(a) * 7.4, 0.42, 22 + Math.sin(a) * 7.4 * 0.7);
    f.material = c.mat("#8a6a44");
    c.add(f);
  }
}

/** ม้านั่งไม้ในเมือง */
function bench(c: PropCtx, x: number, z: number, ry: number): void {
  const bSeat = c.flat(MeshBuilder.CreateBox("bseat", { width: 1.4, height: 0.1, depth: 0.5 }, c.scene));
  bSeat.position.set(x, 0.45, z);
  bSeat.rotation.y = ry;
  bSeat.material = c.mat("#7a522a");
  c.add(bSeat);

  const bBack = c.flat(MeshBuilder.CreateBox("bback", { width: 1.4, height: 0.4, depth: 0.08 }, c.scene));
  const cos = Math.cos(ry), sin = Math.sin(ry);
  bBack.position.set(x - sin * 0.22, 0.72, z - cos * 0.22);
  bBack.rotation.y = ry;
  bBack.material = c.mat("#6a421a");
  c.add(bBack);

  for (const dx of [-0.55, 0.55]) {
    const leg = c.flat(MeshBuilder.CreateBox("bleg", { width: 0.08, height: 0.4, depth: 0.4 }, c.scene));
    leg.position.set(x + cos * dx, 0.2, z - sin * dx);
    leg.rotation.y = ry;
    leg.material = c.mat("#3a2a1a");
    c.add(leg);
  }
}

/** บ้านในเมือง สไตล์ Chibi Fantasy MMORPG */
function house(c: PropCtx, x: number, z: number, ry: number, roof: string): void {
  const w = 3.8 + c.rand() * 1.2;
  const d = 3.2 + c.rand() * 1.0;
  const h = 2.6 + c.rand() * 0.6;

  // 1. ฐานหินรองรับตัวบ้าน (Stone Foundation)
  const base = c.flat(MeshBuilder.CreateBox("hbase", { width: w + 0.3, height: 0.35, depth: d + 0.3 }, c.scene));
  base.position.set(x, 0.17, z);
  base.rotation.y = ry;
  base.material = c.mat("#82796e");
  c.add(base);

  // 2. ตัวบ้านไม้ฉาบปูน
  const body = c.flat(MeshBuilder.CreateBox("hbody", { width: w, height: h, depth: d }, c.scene));
  body.position.set(x, h / 2 + 0.35, z);
  body.rotation.y = ry;
  body.material = c.mat(c.rand() > 0.5 ? "#f3e7ce" : "#e6d6b3");
  c.add(body);

  // 3. คานไม้ประดับมุมบ้าน (Timber framing)
  for (const [cx, cz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]] as const) {
    const post = c.flat(MeshBuilder.CreateBox("hpost", { width: 0.22, height: h, depth: 0.22 }, c.scene));
    const px = Math.cos(ry) * (cx * (w / 2)) - Math.sin(ry) * (cz * (d / 2));
    const pz = Math.sin(ry) * (cx * (w / 2)) + Math.cos(ry) * (cz * (d / 2));
    post.position.set(x + px, h / 2 + 0.35, z + pz);
    post.rotation.y = ry;
    post.material = c.mat("#5a3c22");
    c.add(post);
  }

  // 4. หลังคาจั่วทรงสวยงาม
  const r = c.flat(MeshBuilder.CreateCylinder("hroof", { diameter: Math.max(w, d) * 1.55, height: 2.1, tessellation: 4, diameterTop: 0 }, c.scene));
  r.position.set(x, h + 1.25, z);
  r.rotation.y = ry + Math.PI / 4;
  r.material = c.mat(roof);
  c.add(r);

  // 5. ประตูบ้านไม้
  const door = MeshBuilder.CreateBox("hdoor", { width: 0.85, height: 1.5, depth: 0.1 }, c.scene);
  const fx = Math.sin(ry), fz = Math.cos(ry);
  door.position.set(x + fx * (d / 2 + 0.05), 0.95, z + fz * (d / 2 + 0.05));
  door.rotation.y = ry;
  door.material = c.mat("#5c361e");
  c.add(door);

  // 6. หน้าต่างกระจกอบอุ่น + กระถางดอกไม้หน้าระเบียง
  const sx = Math.cos(ry), sz = -Math.sin(ry);
  const win = MeshBuilder.CreateBox("hwin", { width: 0.65, height: 0.55, depth: 0.1 }, c.scene);
  win.position.set(x + fx * (d / 2 + 0.05) + sx * 1.15, 1.65, z + fz * (d / 2 + 0.05) + sz * 1.15);
  win.rotation.y = ry;
  win.material = c.mat("#ffe7a3", { em: "#e0ad3d", noLight: true });
  c.add(win);

  // กระถางดอกไม้ใต้หน้าต่าง
  const fbox = c.flat(MeshBuilder.CreateBox("hflowerbox", { width: 0.75, height: 0.2, depth: 0.22 }, c.scene));
  fbox.position.set(x + fx * (d / 2 + 0.12) + sx * 1.15, 1.3, z + fz * (d / 2 + 0.12) + sz * 1.15);
  fbox.rotation.y = ry;
  fbox.material = c.mat("#8a5a36");
  c.add(fbox);

  const flowers = c.flat(MeshBuilder.CreateBox("hflowers", { width: 0.7, height: 0.12, depth: 0.18 }, c.scene));
  flowers.position.set(x + fx * (d / 2 + 0.12) + sx * 1.15, 1.42, z + fz * (d / 2 + 0.12) + sz * 1.15);
  flowers.rotation.y = ry;
  flowers.material = c.mat("#ff6b8b", { em: "#e84393" });
  c.add(flowers);

  // 7. ปล่องไฟอิฐ
  const chim = c.flat(MeshBuilder.CreateBox("hchim", { width: 0.5, height: 1.4, depth: 0.5 }, c.scene));
  chim.position.set(x - sx * 0.95, h + 1.4, z - sz * 0.95);
  chim.material = c.mat("#787068");
  c.add(chim);
}

/* ---------------- ของเตี้ย (ต่ำกว่า 1.2) ---------------- */

function lowProp(c: PropCtx, id: string, x: number, z: number, s: number): void {
  const r = c.rand();
  const blob = (hex: string, size: number, y = size * 0.3, opts = {}): Mesh => {
    const b = c.flat(MeshBuilder.CreateIcoSphere("lp", { radius: size, subdivisions: 1 }, c.scene));
    b.position.set(x, y, z);
    b.scaling.y = 0.6;
    b.material = c.mat(hex, opts);
    c.add(b);
    return b;
  };
  const tuft = (hex: string): void => {
    for (let k = 0; k < 3; k++) {
      const g = MeshBuilder.CreateCylinder("tuft", { diameterTop: 0, diameterBottom: 0.16 * s, height: 0.6 * s, tessellation: 3 }, c.scene);
      g.position.set(x + (k - 1) * 0.18, 0.3 * s, z + (k % 2) * 0.15);
      g.rotation.z = (k - 1) * 0.35;
      g.material = c.mat(hex);
      c.add(g);
    }
  };
  switch (id) {
    case "desert":
      if (r < 0.4) blob("#d8b276", 0.9 * s, 0.1); // เนินทราย
      else if (r < 0.6) { // กระดูก
        const bone = MeshBuilder.CreateCylinder("bone", { diameter: 0.12, height: 1.1 * s, tessellation: 4 }, c.scene);
        bone.position.set(x, 0.08, z); bone.rotation.z = Math.PI / 2; bone.rotation.y = r * 9;
        bone.material = c.mat("#f2ead8"); c.add(bone);
      } else tuft("#a59a4f");
      return;
    case "snow":
    case "dungeon_frost":
      if (r < 0.5) blob("#f7fbff", 0.8 * s, 0.05);
      else blob("#bfe4f7", 0.4 * s, 0.2, { em: "#5b8fb0" }); // ก้อนน้ำแข็ง
      return;
    case "volcano":
      if (r < 0.4) { // บ่อลาวา
        const d = MeshBuilder.CreateDisc("lava", { radius: 1.1 * s, tessellation: 7 }, c.scene);
        d.rotation.x = Math.PI / 2; d.position.set(x, 0.03, z);
        d.material = c.mat("#ff5a1f", { em: "#ff3d00", noLight: true }); c.add(d);
      } else blob("#2a1c17", 0.5 * s);
      return;
    case "swamp":
      if (r < 0.35) { // แอ่งน้ำ
        const d = MeshBuilder.CreateDisc("puddle", { radius: 1.4 * s, tessellation: 8 }, c.scene);
        d.rotation.x = Math.PI / 2; d.position.set(x, 0.03, z);
        d.material = c.mat("#3b5246", { alpha: 0.9 }); c.add(d);
        const lily = MeshBuilder.CreateDisc("lily", { radius: 0.3, tessellation: 6 }, c.scene);
        lily.rotation.x = Math.PI / 2; lily.position.set(x + 0.4, 0.05, z - 0.2);
        lily.material = c.mat("#6fae5a"); c.add(lily);
      } else tuft("#7a8a4a"); // กก
      return;
    case "coast":
      if (r < 0.3) blob("#f4e9cf", 0.25 * s, 0.08); // เปลือกหอย
      else if (r < 0.55) { // ขอนลอยน้ำ
        const log = c.flat(MeshBuilder.CreateCylinder("drift", { diameter: 0.3, height: 1.6 * s, tessellation: 5 }, c.scene));
        log.position.set(x, 0.15, z); log.rotation.z = Math.PI / 2; log.rotation.y = r * 7;
        log.material = c.mat("#b9a07c"); c.add(log);
      } else tuft("#9db86a");
      return;
    case "crystal_cave":
    case "root_cave":
    case "dungeon_mine":
    case "abyss_ruins": {
      const glow = id === "crystal_cave" ? "#7fe0ff" : id === "root_cave" ? "#7dff9a" : id === "abyss_ruins" ? "#b388ff" : "#ffc76b";
      if (r < 0.5) {
        const sh = c.flat(MeshBuilder.CreateCylinder("shard", { diameterTop: 0, diameterBottom: 0.35 * s, height: 0.9 * s, tessellation: 4 }, c.scene));
        sh.position.set(x, 0.45 * s, z); sh.rotation.z = (r - 0.25) * 1.2;
        sh.material = c.mat(glow, { em: glow, noLight: true }); c.add(sh);
      } else blob("#3a3530", 0.5 * s);
      return;
    }
    case "graveyard":
      if (r < 0.5) { // ป้ายหลุมศพ
        const st = c.flat(MeshBuilder.CreateBox("tomb", { width: 0.6 * s, height: 0.9 * s, depth: 0.18 }, c.scene));
        st.position.set(x, 0.45 * s, z); st.rotation.y = r * 6; st.rotation.z = (r - 0.25) * 0.3;
        st.material = c.mat("#8a8a8c"); c.add(st);
      } else if (r < 0.7) { // ไม้กางเขนเก่า
        const v = MeshBuilder.CreateBox("crossv", { width: 0.12, height: 1, depth: 0.12 }, c.scene);
        v.position.set(x, 0.5, z); v.material = c.mat("#5a4632"); c.add(v);
        const h = MeshBuilder.CreateBox("crossh", { width: 0.55, height: 0.1, depth: 0.1 }, c.scene);
        h.position.set(x, 0.75, z); h.material = c.mat("#5a4632"); c.add(h);
      } else tuft("#6a6a50");
      return;
    case "jungle":
      if (r < 0.55) { // เฟิร์นใบใหญ่
        for (let k = 0; k < 4; k++) {
          const leaf = MeshBuilder.CreateCylinder("fern", { diameterTop: 0, diameterBottom: 0.35 * s, height: 1.0 * s, tessellation: 3 }, c.scene);
          leaf.position.set(x, 0.4 * s, z);
          leaf.rotation.z = 0.9; leaf.rotation.y = (k / 4) * Math.PI * 2;
          leaf.material = c.mat(k % 2 ? "#3f8f3a" : "#2f7a2f"); c.add(leaf);
        }
      } else if (r < 0.75) { // ดอกไม้ป่าสีจัด
        const f = MeshBuilder.CreateSphere("jflower", { diameter: 0.35, segments: 3 }, c.scene);
        f.position.set(x, 0.3, z); f.material = c.mat(r < 0.65 ? "#ff5a8a" : "#ffb03a"); c.add(f);
      } else tuft("#3f9a3a");
      return;
    case "storm":
      if (r < 0.5) blob("#5a6070", 0.6 * s);
      else tuft("#8a9070");
      return;
    case "celestial":
      if (r < 0.45) blob("#ffffff", 0.9 * s, 0.15, { alpha: 0.85 }); // ก้อนเมฆบนพื้น
      else if (r < 0.7) blob("#ffe08a", 0.25 * s, 0.25, { em: "#ffd040", noLight: true }); // ประกายดาว
      else tuft("#e8d890");
      return;
    case "ruins":
      if (r < 0.5) { // อิฐแตก
        const b = c.flat(MeshBuilder.CreateBox("brick", { width: 0.8 * s, height: 0.35, depth: 0.5 * s }, c.scene));
        b.position.set(x, 0.17, z); b.rotation.y = r * 9;
        b.material = c.mat("#a8a290"); c.add(b);
      } else tuft("#8a9a6a");
      return;
    default: { // ทุ่ง เนิน ป่า เมือง: พุ่มไม้ เห็ด หญ้า
      if (r < 0.45) {
        const bush = blob(id === "forest" ? "#2f6b2c" : "#4f9a48", 0.7 * s, 0.35 * s);
        if (c.rand() < 0.4) {
          const berry = MeshBuilder.CreateSphere("berry", { diameter: 0.16, segments: 3 }, c.scene);
          berry.position.set(bush.position.x + 0.3, 0.6 * s, z + 0.3);
          berry.material = c.mat("#e04b5a"); c.add(berry);
        }
      } else if (r < 0.65) {
        const stem = MeshBuilder.CreateCylinder("mstem", { diameter: 0.12, height: 0.3, tessellation: 5 }, c.scene);
        stem.position.set(x, 0.15, z); stem.material = c.mat("#f0e6d0"); c.add(stem);
        const cap = MeshBuilder.CreateSphere("mcap", { diameter: 0.4, segments: 3, slice: 0.5 }, c.scene);
        cap.position.set(x, 0.28, z); cap.material = c.mat(c.rand() > 0.5 ? "#d9473f" : "#c9873f"); c.add(cap);
      } else tuft(id === "forest" ? "#3d7a35" : "#5fae4f");
    }
  }
}

/* ---------------- ของสูง ---------------- */

function tallProp(c: PropCtx, id: string, x: number, z: number, s: number): void {
  const r = c.rand();
  switch (id) {
    case "ruins":
    case "abyss_ruins": { // ซากกำแพง
      const col = id === "ruins" ? "#9a9686" : "#2a2140";
      const w = c.flat(MeshBuilder.CreateBox("wall", { width: 3.2 * s, height: (1.4 + r * 1.6) * s, depth: 0.6 }, c.scene));
      w.position.set(x, w.getBoundingInfo().boundingBox.extendSize.y, z); w.rotation.y = r * 6;
      w.material = c.mat(col); c.add(w);
      return;
    }
    case "crystal_cave": { // กองผลึก
      for (let k = 0; k < 4; k++) {
        const col = ["#4fd1c5", "#b794f4", "#63b3ed"][k % 3]!;
        const h = (1.2 + c.rand() * 1.8) * s;
        const cr = c.flat(MeshBuilder.CreateCylinder("crys2", { diameterTop: 0, diameterBottom: 0.6 * s, height: h, tessellation: 6 }, c.scene));
        cr.position.set(x + (c.rand() - 0.5) * 1.4, h / 2, z + (c.rand() - 0.5) * 1.4);
        cr.rotation.x = (c.rand() - 0.5) * 0.6; cr.rotation.z = (c.rand() - 0.5) * 0.6;
        cr.material = c.mat(col, { em: col, noLight: true }); c.add(cr);
      }
      return;
    }
    case "snow":
    case "dungeon_frost": { // ตุ๊กตาหิมะ/ก้อนน้ำแข็งใหญ่
      const ice = c.flat(MeshBuilder.CreateCylinder("ice", { diameterTop: 0.4 * s, diameterBottom: 1.4 * s, height: 2.4 * s, tessellation: 5 }, c.scene));
      ice.position.set(x, 1.2 * s, z); ice.rotation.z = (r - 0.5) * 0.4;
      ice.material = c.mat("#cfeaf8", { em: "#6a9cbf", alpha: 0.9 }); c.add(ice);
      return;
    }
    case "volcano":
    case "desert":
    case "dungeon_mine":
    case "root_cave": { // หินผาใหญ่
      const col = { volcano: "#3a2620", desert: "#c08a55", dungeon_mine: "#4a3c31", root_cave: "#4a3b2c" }[id]!;
      const b = c.flat(MeshBuilder.CreateIcoSphere("boulder", { radius: 1.4 * s, subdivisions: 1 }, c.scene));
      b.position.set(x, 0.8 * s, z); b.scaling.y = 1.1; b.rotation.y = r * 5;
      b.material = c.mat(col); c.add(b);
      return;
    }
    case "graveyard": { // สุสานหินเตี้ย
      const base = c.flat(MeshBuilder.CreateBox("crypt", { width: 2.2 * s, height: 1.6 * s, depth: 1.6 * s }, c.scene));
      base.position.set(x, 0.8 * s, z); base.rotation.y = r * 6;
      base.material = c.mat("#6a6a6e"); c.add(base);
      const roof = c.flat(MeshBuilder.CreateCylinder("croof", { diameterTop: 0, diameterBottom: 2.6 * s, height: 0.9 * s, tessellation: 4 }, c.scene));
      roof.position.set(x, 2.05 * s, z); roof.rotation.y = r * 6 + Math.PI / 4;
      roof.material = c.mat("#4a4a50"); c.add(roof);
      return;
    }
    case "celestial": { // เสาหินขาว ลูกแก้วแสงลอยบนยอด
      const h = (2 + r * 1.5) * s;
      const p = c.flat(MeshBuilder.CreateCylinder("cpillar", { diameter: 0.8 * s, height: h, tessellation: 8 }, c.scene));
      p.position.set(x, h / 2, z); p.material = c.mat("#f4f0e4"); c.add(p);
      const orb = MeshBuilder.CreateSphere("corb", { diameter: 0.6 * s, segments: 4 }, c.scene);
      orb.position.set(x, h + 0.6 * s, z); orb.material = c.mat("#ffe08a", { em: "#ffc840", noLight: true }); c.add(orb);
      return;
    }
    case "storm": { // แท่งเหล็กล่อฟ้า
      const rod = c.flat(MeshBuilder.CreateCylinder("rod", { diameterTop: 0.05, diameterBottom: 0.35 * s, height: 3.4 * s, tessellation: 5 }, c.scene));
      rod.position.set(x, 1.7 * s, z); rod.rotation.z = (r - 0.5) * 0.2;
      rod.material = c.mat("#4a4f5a"); c.add(rod);
      const tip = MeshBuilder.CreateSphere("rodtip", { diameter: 0.3, segments: 3 }, c.scene);
      tip.position.set(x, 3.45 * s, z); tip.material = c.mat("#bfe0ff", { em: "#8ac8ff", noLight: true }); c.add(tip);
      return;
    }
    case "jungle": { // เห็ดยักษ์
      const st = c.flat(MeshBuilder.CreateCylinder("gmstem", { diameter: 0.5 * s, height: 1.8 * s, tessellation: 6 }, c.scene));
      st.position.set(x, 0.9 * s, z); st.material = c.mat("#efe4cc"); c.add(st);
      const cap = MeshBuilder.CreateSphere("gmcap", { diameter: 2.2 * s, segments: 4, slice: 0.5 }, c.scene);
      cap.position.set(x, 1.75 * s, z); cap.material = c.mat(r > 0.5 ? "#c44a3a" : "#e08a2a"); c.add(cap);
      return;
    }
    case "swamp": { // ตอไม้ตายยืนต้น
      const t = c.flat(MeshBuilder.CreateCylinder("deadtree", { diameterTop: 0.2, diameterBottom: 0.7 * s, height: 3 * s, tessellation: 5 }, c.scene));
      t.position.set(x, 1.5 * s, z); t.rotation.z = (r - 0.5) * 0.3;
      t.material = c.mat("#3a3128"); c.add(t);
      const br = c.flat(MeshBuilder.CreateCylinder("branch", { diameter: 0.15, height: 1.3 * s, tessellation: 4 }, c.scene));
      br.position.set(x + 0.4 * s, 2.2 * s, z); br.rotation.z = -0.9;
      br.material = c.mat("#3a3128"); c.add(br);
      return;
    }
    case "coast": { // หินริมหาดซ้อนกัน
      for (let k = 0; k < 3; k++) {
        const b = c.flat(MeshBuilder.CreateIcoSphere("cstone", { radius: (0.9 - k * 0.22) * s, subdivisions: 1 }, c.scene));
        b.position.set(x, (0.5 + k * 0.75) * s, z); b.scaling.y = 0.7;
        b.material = c.mat("#9c968a"); c.add(b);
      }
      return;
    }
    default: { // ขอนไม้ล้ม + ตอไม้ (ทุ่ง เนิน ป่า เมือง)
      if (r < 0.5) {
        const log = c.flat(MeshBuilder.CreateCylinder("log", { diameter: 0.7 * s, height: 3 * s, tessellation: 6 }, c.scene));
        log.position.set(x, 0.35 * s, z); log.rotation.z = Math.PI / 2; log.rotation.y = r * 8;
        log.material = c.mat("#6b4a2e"); c.add(log);
        const moss = c.flat(MeshBuilder.CreateIcoSphere("moss", { radius: 0.4 * s, subdivisions: 1 }, c.scene));
        moss.position.set(x, 0.7 * s, z); moss.scaling.y = 0.4;
        moss.material = c.mat("#4f8a3c"); c.add(moss);
      } else {
        // หินใหญ่มีมอส (สูงเกิน 1.2 จึงต้องผ่าน blocked มาแล้ว)
        const b = c.flat(MeshBuilder.CreateIcoSphere("boulder", { radius: 1.2 * s, subdivisions: 1 }, c.scene));
        b.position.set(x, 0.7 * s, z); b.rotation.y = r * 5;
        b.material = c.mat("#8e9196"); c.add(b);
        const top = c.flat(MeshBuilder.CreateIcoSphere("moss", { radius: 0.8 * s, subdivisions: 1 }, c.scene));
        top.position.set(x, 1.5 * s, z); top.scaling.y = 0.35;
        top.material = c.mat(id === "forest" ? "#3d6b33" : "#5c9a4a"); c.add(top);
      }
    }
  }
}
